import classNames from "classnames"
import { useCallback, useEffect } from "react"
import { useField, useFormikContext } from "formik"
import { Checkbox as MarmaladeCheckbox } from "@eventbrite/marmalade"
import { Typography } from "@eventbrite/marmalade"

// styles
import * as styles from "./Checkbox.module.scss"

type HandleChange = (
  checked: boolean,
  value: string,
  event: React.ChangeEvent<HTMLInputElement>
) => void

// @TODO: need a better way to reference these props
export interface CheckboxProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  | "aria-label"
  | "value"
  | "defaultValue"
  | "onChange"
  | "hasError"
  | "shouldHighlightError"
  | "checked"
  | "size"
> {
  /**
   * Name of the checkbox
   */
  name: string
  /**
   * ID of checkbox component's wrapper
   */
  id?: string
  /**
   * Initial checkbox checked state when it is created.
   * It will not override the state after it's been created.
   */
  defaultValue?: boolean
  /**
   * The checkbox's checked state, overriding any existing state.
   */
  checked?: boolean
  /**
   * The value of the checkbox
   */
  value?: string
  /**
   * Value of if the checkbox is disabled
   */
  disabled?: boolean
  /**
   * Function to be invoked when checkbox checked state changes.
   * Passes the checked state and checkbox value
   */
  onChange?: (
    checked: boolean,
    value: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void
  /** Size of the input element */
  size?: "small" | "large"
  "aria-label"?: string

  labelText?: string | React.ReactNode
  rightSideCheckbox?: boolean
}

export const Checkbox: React.FC<CheckboxProps> = (props) => {
  const { labelText, onChange, rightSideCheckbox, name, ...otherProps } = props

  const [field, meta, helpers] = useField(name)
  const { submitCount } = useFormikContext()
  const showError = (meta.touched || submitCount > 0) && !!meta.error

  const handleChange: HandleChange = useCallback(
    (value, name, event) => {
      helpers.setTouched(true, false)

      if (onChange) {
        onChange(value, name, event)
      } else {
        helpers.setValue(value, true)
      }
    },
    [onChange, helpers]
  )

  useEffect(() => {
    if (field.value !== true && field.value !== false) {
      helpers.setValue(false, false)
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (typeof window !== "undefined") {
        const checkBoxSvg = document?.querySelector(
          `.${styles.checkboxWrapper} svg`
        )

        if (checkBoxSvg) {
          checkBoxSvg.setAttribute("viewBox", "0 0 24 24")
        }
      }
    }, 1000)

    return () => clearTimeout(timer)
  }, [])

  return (
    <div
      id="checkboxWrapperId"
      className={classNames(styles.checkboxWrapper, props.className, {
        [`${styles.rightAlignedCheckbox}`]: props.rightSideCheckbox
      })}
      style={{ display: "flex", flexDirection: "column" }}
    >
      <div style={{ display: "flex", alignItems: "flex-start" }}>
        <MarmaladeCheckbox
          {...otherProps}
          onChange={handleChange}
          value={field.value}
        />
        {labelText ? (
          <Typography as="p" variant="body-md" className={styles.label}>
            {labelText}
          </Typography>
        ) : null}
      </div>
      {showError && (
        <Typography
          as="p"
          variant="body-sm"
          className={styles.errorText}
        >
          {meta.error}
        </Typography>
      )}
    </div>
  )
}
